import { lazy, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import ProtectedRoute from "./ProtectedRoute";
import { ROUTES } from "./routes";
import AuthCallbackResponse from "@/pages/AuthCallback/AuthCallbackResponse";
import AppLoader from "@/components/AppLoader/AppLoader";
import { ENV_ENABLE_DASHBOARD } from "@/env";
import { ENABLE_FEATURE_USER_MANAGEMENT } from "@/env";
import { hasOrgAdminRole } from "@/auth0/accessControl";
import {
  buildInvitationQueryString,
  extractInvitationParams,
} from "@/auth0/invitationParamHandler";
import PageTitle from "./PageTitle";
import type { OrgDataType } from "@/types/profileSettings";

const ProfileSettings = lazy(
  () => import("../pages/ProfileSettings/ProfileSettings"),
);
const Dashboard = lazy(() => import("../pages/Dashboard/Dashboard"));

const MfaManageModal = lazy(() =>
  import("@/pages/ProfileSettings/MfaSettings/MfaManageModal/MfaManageModal").then(
    (module) => ({ default: module.MfaManageModal }),
  ),
);

const UserManagement = lazy(
  () => import("../pages/UserManagement/UserManagement"),
);

const RouteLoader = () => <AppLoader />;

const DashboardRedirect = () => {
  const { lang } = useParams<{ lang?: string }>();
  const location = useLocation();
  const targetRoute = ENV_ENABLE_DASHBOARD ? ROUTES.DASHBOARD : ROUTES.PROFILE_SETTINGS;
  const invitationParams = extractInvitationParams(
    new URLSearchParams(location.search),
  );
  const invitationSearch = invitationParams
    ? buildInvitationQueryString(invitationParams)
    : "";

  const basePath = lang ? `/${lang}${targetRoute}` : targetRoute;
  const redirectPath = invitationSearch ? `${basePath}?${invitationSearch}` : basePath;
  return (
    <Navigate
      to={redirectPath}
      replace
    />
  );
};

const UserManagementRoute = () => {
  const { user } = useAuth0();
  const { lang } = useParams<{ lang?: string }>();
  const context = useOutletContext<OrgDataType>();

  if (context?.isOrgRolesLoading) {
    return <RouteLoader />;
  }

  if (!hasOrgAdminRole(user, context.orgAdminRoles)) {
    const callbackPath = lang
      ? `/${lang}${ROUTES.CALLBACK}`
      : ROUTES.CALLBACK;
    const unauthorizedQuery = "status=failed&error=unauthorized_client";

    return <Navigate to={`${callbackPath}?${unauthorizedQuery}`} replace />;
  }

  return (
    <Suspense fallback={<RouteLoader />}>
      <UserManagement orgAdminRoles={context.orgAdminRoles} />
    </Suspense>
  );
};

const ProfileSettingsRoute = () => {
  const context = useOutletContext<OrgDataType>();
  return <ProfileSettings mfaRequiredFlag={context?.mfaRequiredFlag} />;
};

const AppRoutes = () => {
  return (
    <>
      <PageTitle />
      <Routes>
        {/* Dedicated callback route for Auth0 to redirect back to after login.
    Keeps Auth0's code exchange isolated from protected routes. */}
        <Route
          path={`:lang?${ROUTES.CALLBACK}`}
          element={<AuthCallbackResponse />}
        />

        <Route element={<ProtectedRoute />}>
          {ENV_ENABLE_DASHBOARD && (
            <Route
              path={`:lang?${ROUTES.DASHBOARD}`}
              element={
                <Suspense fallback={<RouteLoader />}>
                  <Dashboard />
                </Suspense>
              }
            />
          )}
          <Route
            path={`:lang?${ROUTES.PROFILE_SETTINGS}`}
            element={
              <Suspense fallback={<RouteLoader />}>
                <ProfileSettingsRoute />
              </Suspense>
            }
          />
          <Route
            path={`:lang?${ROUTES.MFA}`}
            element={
              <Suspense fallback={<RouteLoader />}>
                <MfaManageModal />
              </Suspense>
            }
          />
          <Route
            path={`:lang?${ROUTES.UPDATE_PHONE}`}
            element={
              <Suspense fallback={<RouteLoader />}>
                <MfaManageModal />
              </Suspense>
            }
          />
          {ENABLE_FEATURE_USER_MANAGEMENT && (
            <Route
              path={`:lang?${ROUTES.USER_MANAGEMENT}`}
              element={<UserManagementRoute />}
            />
          )}
        </Route>
        <Route
          path="*"
          element={<DashboardRedirect />}
        />
      </Routes>
    </>
  );
};

export default AppRoutes;
